import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PARENT_GOALS = [
  { emoji: '🌟', label: 'Build academic confidence',  value: 'Build academic confidence' },
  { emoji: '📈', label: 'Catch up on a subject',      value: 'Catch up on a subject' },
  { emoji: '🚀', label: 'Get ahead',                  value: 'Get ahead' },
  { emoji: '❤️', label: 'Develop a love of learning', value: 'Develop a love of learning' },
  { emoji: '📝', label: 'Prepare for tests',           value: 'Prepare for tests' },
];

export default function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'role' | 'details' | 'goal'>('role');
  const [parentGoal, setParentGoal] = useState('');
  const [role, setRole] = useState<'student' | 'parent' | null>(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const age = birthYear ? currentYear - parseInt(birthYear) : null;
  const isMinor = age !== null && age < 13;

  const handleRoleSelect = (r: 'student' | 'parent') => {
    setRole(r);
    setStep('details');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!role) return;
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (role === 'student' && !birthYear) { setError('Please enter your birth year.'); return; }

    // COPPA gate: under-13 students must be set up by a parent
    if (role === 'student' && isMinor) {
      setError('Students under 13 must have their account created by a parent or guardian. Please ask your parent to sign up as a Parent and add you as a child.');
      return;
    }

    setLoading(true);
    try {
      await signUp(email.trim().toLowerCase(), password, role, isMinor ?? false, firstName.trim());
      if (role === 'parent') {
        setLoading(false);
        setStep('goal');
        return;
      }
      navigate(`/verify?email=${encodeURIComponent(email)}&role=${role}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign up failed. Please try again.';
      if (msg.includes('already exists')) {
        setError('An account with this email already exists. Try signing in.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <Link to="/" style={s.homeLink}>← Home</Link>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.logoMark}>🎓</div>
          <div>
            <div style={s.logoTitle}>Academic Allies</div>
            <div style={s.logoSub}>AI Learning Center</div>
          </div>
        </div>

        <h1 style={s.heading}>Create your account</h1>

        {step === 'role' && (
          <>
            <p style={s.sub}>Who are you signing up as?</p>
            <div style={s.roleGrid}>
              <button style={s.roleCard} onClick={() => handleRoleSelect('student')}>
                <span style={s.roleIcon}>🎓</span>
                <span style={s.roleName}>Student</span>
                <span style={s.roleDesc}>I'm here to get help with school</span>
              </button>
              <button style={s.roleCard} onClick={() => handleRoleSelect('parent')}>
                <span style={s.roleIcon}>👨‍👩‍👧</span>
                <span style={s.roleName}>Parent / Guardian</span>
                <span style={s.roleDesc}>I'm managing my child's education</span>
              </button>
            </div>
            <div style={s.footer}>
              Already have an account? <Link to="/login" style={s.link}>Sign in</Link>
            </div>
          </>
        )}

        {step === 'goal' && (
          <>
            <p style={s.sub}>One last thing, {firstName}!</p>
            <p style={{ ...s.sub, marginBottom: '20px', fontSize: '13px', lineHeight: 1.65 }}>
              What's your main goal for your child? This personalizes everything — their teachers, first session, and Mrs. Kei's weekly letters to you.
            </p>
            <div style={s.goalGrid}>
              {PARENT_GOALS.map(g => (
                <button
                  key={g.value}
                  style={{ ...s.goalCard, ...(parentGoal === g.value ? s.goalCardActive : {}) }}
                  onClick={() => setParentGoal(g.value)}
                >
                  <span style={s.goalEmoji}>{g.emoji}</span>
                  <span style={s.goalLabel}>{g.label}</span>
                </button>
              ))}
            </div>
            <button
              style={{ ...s.btn, marginTop: '8px', opacity: parentGoal ? 1 : 0.5 }}
              onClick={() => {
                if (parentGoal) localStorage.setItem('aa_parent_goal_pending', parentGoal);
                navigate(`/verify?email=${encodeURIComponent(email)}&role=parent`);
              }}
            >
              {parentGoal ? 'Continue to verify email →' : 'Skip for now →'}
            </button>
          </>
        )}

        {step === 'details' && (
          <>
            <p style={s.sub}>
              Signing up as a <strong style={{ color: '#4F46E5' }}>{role}</strong>
              <button style={s.changeRole} onClick={() => setStep('role')}>Change</button>
            </p>

            {error && <div style={s.errorBox} role="alert">{error}</div>}

            <form onSubmit={handleSubmit} style={s.form}>
              <label style={s.label}>First name</label>
              <input type="text" required autoFocus value={firstName} onChange={e => setFirstName(e.target.value)} style={s.input} placeholder="e.g. Alex" maxLength={50} />

              <label style={s.label}>Email address</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={s.input} placeholder="you@example.com" />

              {role === 'student' && (
                <>
                  <label style={s.label}>Year of birth</label>
                  <input type="number" required min="1990" max={currentYear - 5} value={birthYear} onChange={e => setBirthYear(e.target.value)} style={s.input} placeholder={`e.g. ${currentYear - 14}`} />
                  {age !== null && age >= 13 && age < 20 && (
                    <div style={s.ageNote}>Age {age} — eligible for a student account</div>
                  )}
                </>
              )}

              <label style={s.label}>Password</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} style={s.input} placeholder="Min. 8 chars, uppercase + number" />

              <label style={s.label}>Confirm password</label>
              <input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)} style={s.input} placeholder="••••••••" />

              <p style={s.terms}>
                By creating an account you agree to our <a href="/eula" style={s.link}>Terms of Use</a> and <a href="/privacy" style={s.link}>Privacy Policy</a>.
              </p>

              <button type="submit" disabled={loading} style={s.btn}>
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <div style={s.footer}>
              Already have an account? <Link to="/login" style={s.link}>Sign in</Link>
            </div>
          </>
        )}
      </div>

      <style>{`
        input:focus { border-color: #4F46E5 !important; outline: none; box-shadow: 0 0 0 3px rgba(79,70,229,0.15) !important; }
        button:not(:disabled):hover { opacity: 0.9; }
      `}</style>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#F4F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative' },
  homeLink: { position: 'absolute', top: '20px', left: '24px', fontSize: '13px', fontWeight: 600, color: '#64748B', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' },
  card: { background: '#fff', borderRadius: '16px', boxShadow: '0 4px 32px rgba(79,70,229,0.1)', padding: '40px', width: '100%', maxWidth: '440px' },
  logo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' },
  logoMark: { width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' },
  logoTitle: { fontSize: '15px', fontWeight: 800, color: '#1E1B4B', letterSpacing: '-0.01em' },
  logoSub: { fontSize: '10px', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' },
  heading: { fontSize: '22px', fontWeight: 800, color: '#1E1B4B', letterSpacing: '-0.02em', marginBottom: '4px' },
  sub: { fontSize: '14px', color: '#64748B', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' },
  roleGrid: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' },
  roleCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px',
    background: '#F8FAFF', border: '2px solid #E2E8F0', borderRadius: '12px',
    padding: '16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
  },
  roleIcon: { fontSize: '24px' },
  roleName: { fontSize: '14px', fontWeight: 700, color: '#1E1B4B' },
  roleDesc: { fontSize: '12px', color: '#64748B' },
  changeRole: { background: 'none', border: 'none', color: '#4F46E5', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: '0', marginLeft: 'auto' },
  form: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: 600, color: '#374151', marginTop: '12px', marginBottom: '4px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', color: '#111827', transition: 'border-color 0.15s', boxSizing: 'border-box' },
  ageNote: { fontSize: '11px', color: '#059669', fontWeight: 600, marginTop: '4px' },
  errorBox: { background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#991B1B', marginBottom: '12px' },
  terms: { fontSize: '11px', color: '#94A3B8', marginTop: '12px', lineHeight: 1.5 },
  btn: { marginTop: '16px', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.15s' },
  footer: { fontSize: '13px', color: '#64748B', textAlign: 'center', marginTop: '20px' },
  link: { color: '#4F46E5', fontWeight: 600, textDecoration: 'none' },
  goalGrid: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' },
  goalCard: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 16px', borderRadius: '10px',
    border: '2px solid #E2E8F0', background: '#F8FAFF',
    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
  },
  goalCardActive: { border: '2px solid #4F46E5', background: '#EEF2FF' },
  goalEmoji: { fontSize: '22px', flexShrink: 0 },
  goalLabel: { fontSize: '14px', fontWeight: 600, color: '#1E1B4B' },
};
